// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import SearchSiteItem from 'components/molecules/RouteSetting/SearchSiteItem';
import SiteItem from 'components/molecules/RouteSetting/SiteItem';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { IconAddCircleOutline } from 'components/atoms/icons/IconAddCircleOutline';
import { IconSearch } from 'components/atoms/icons/IconSearch';
import RouteItem from 'components/molecules/RouteSetting/RouteItem';
import {
  CollectRoute,
  CollectRouteDetail,
  EmissionSite,
  RouteCreateEditModalType,
  RouteForm,
  RouteFormUpdate,
} from 'types/routeManagementTypes';
import { ModalType } from 'util/Enums';
import {
  getCollectRoutes,
  createCollectRoute,
  getCollectRouteDetail,
  updateCollectRoute,
  getEmissionSites,
  addCollectRouteSites,
  deleteRoute,
} from 'apis/collect_route';
import { toast } from 'react-toastify';
import CreateUpdateRouteModal from '../CreateUpdateRouteModal/CreateUpdateRouteModal';

import _debounce from 'lodash/debounce';
import { IconClose } from 'components/atoms/icons/IconClose';
import DeleteItemDarkModal from 'components/molecules/DeleteItemDarkModal';
import Popover from 'components/atoms/Popover';

const RouteSettingManagement = () => {
  const { t } = useTranslation();

  const getRoutes = async (nameToSelect?: string) => {
    await getCollectRoutes({
      params: {
        keyword: keywordRoute,
        includeDeletedSite: true,
        screen: 'setting',
      },
      successCallback: (data) => {
        setRoutes(data);
        if (nameToSelect) {
          const foundRoute = data.find(
            (route) => route.routeName.trim() === nameToSelect.trim()
          );
          if (foundRoute) {
            setSelectedRouteId(foundRoute.id);
          }
        } else if (data.length > 0) {
          setSelectedRouteId(data[0].id);
        }
      },
    });
  };

  const getRouteDetail = async () => {
    await getCollectRouteDetail({
      id: selectedRouteId || 0,
      successCallback: (data) => {
        setRouteDetail(data);
        setSites(data.emissionSites);
      },
    });
  };

  const searchEmissionSites = async () => {
    if (!routeDetail) {
      setEmissionSites([]);
      return;
    }

    await getEmissionSites({
      query: {
        siteId: routeDetail.siteId,
        keyword: keywordSite,
        materialNames: routeDetail.materialNames.join(', '),
      },

      successCallback: (data) => {
        setEmissionSites(data);
      },
    });
  };

  const [routes, setRoutes] = useState<CollectRoute[]>([]);

  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);

  const [routeDetail, setRouteDetail] = useState<CollectRouteDetail | null>(
    null
  );

  const [sites, setSites] = useState<EmissionSite[]>([]);

  const [keywordSite, setKeywordSite] = useState<string>('');
  const [keywordRoute, setKeywordRoute] = useState<string>('');

  const searchSiteInputRef = useRef<HTMLInputElement>(null);
  const routePointsRef = useRef<HTMLDivElement>(null);
  const searchSitesRef = useRef<HTMLDivElement>(null);
  const routeItemsRef = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const [emissionSites, setEmissionSites] = useState<EmissionSite[]>([]);

  const [createUpdateModal, setCreateUpdateModal] =
    useState<RouteCreateEditModalType>({
      isOpen: false,
      type: ModalType.add,
    });

  const [confirmDeleteModal, setConfirmDeleteModal] = useState<
    | {
        routeId: number;
        routeName: string;
      }
    | undefined
  >();

  const handleDragEnd = (result) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(sites);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSites(items);
  };

  const setFormModal = (open: boolean, type: ModalType) => {
    setCreateUpdateModal({
      isOpen: open,
      type: type,
    });
  };

  const handleSubmit = (
    form: RouteForm | RouteFormUpdate,
    routeId?: number
  ) => {
    const formFormat = {
      ...form,
      name: form.name.trim(),
      category: form.category.trim(),
    };

    if (routeId) {
      updateCollectRoute({
        id: routeId.toString(),
        params: formFormat as RouteFormUpdate,
        successCallback: () => {
          toast.success(t('messages.M_011'));
          setFormModal(false, ModalType.add);
          getRoutes(formFormat.name);
          getRouteDetail();
        },
      });
      return;
    }

    createCollectRoute({
      params: formFormat as RouteForm,
      successCallback: () => {
        toast.success(t('messages.M_010'));
        setFormModal(false, ModalType.add);
        getRoutes(formFormat.name);
      },
    });
  };

  const handleAddRoute = () => {
    setFormModal(true, ModalType.add);
  };

  const handleAddSite = (site: EmissionSite) => {
    if (sites.find((s) => s.id === site.id)) {
      return;
    }
    setSites([...sites, site]);
  };

  const handleRemoveSite = (siteId: string) => {
    const updatedSites = sites.filter((site) => site.id !== siteId);
    setSites(updatedSites);
  };

  const saveCollectRouteSites = () => {
    addCollectRouteSites({
      routeId: selectedRouteId || 0,
      siteIds: sites.map((site) => site.id),
      successCallback: () => {
        toast.success(t('messages.M_011'));
        if (routeDetail) {
          getRoutes(routeDetail.routeName);
        }
      },
    });
  };

  const handleEditRoute = async (routeId: number) => {
    await getCollectRouteDetail({
      id: routeId,
      successCallback: (data) => {
        setCreateUpdateModal({
          isOpen: true,
          type: ModalType.update,
          routeEditing: {
            id: data.id,
            name: data.routeName,
            category: data.category.name,
            siteId: data.siteId,
            driverIds: data.drivers.map((driver) => driver.id.toString()),
            materials: data.materialNames,
          },
        });
      },
    });
  };

  const handleDeleteRoute = () => {
    if (confirmDeleteModal?.routeId) {
      deleteRoute({
        routeId: confirmDeleteModal.routeId,
        successCallback: () => {
          toast.success(t('messages.M_012'));
          if (selectedRouteId === confirmDeleteModal.routeId) {
            setSelectedRouteId(null);
          }
          setConfirmDeleteModal(undefined);
          getRoutes();
        },
      });
    }
  };

  const debouncedSetKeywordRoute = useMemo(
    () =>
      _debounce((value: string) => {
        setKeywordRoute(value);
      }, 500),
    []
  );

  const debouncedSetKeywordSite = useMemo(
    () =>
      _debounce((value: string) => {
        setKeywordSite(value);
      }, 500),
    []
  );

  const handleOnchangeKeywordRoute = (value: string) => {
    debouncedSetKeywordRoute(value);
  };

  const handleOnchangeKeywordSite = (value: string) => {
    debouncedSetKeywordSite(value);
  };

  useEffect(() => {
    getRoutes();
  }, []);

  useEffect(() => {
    getRoutes();
  }, [keywordRoute]);

  useEffect(() => {
    if (selectedRouteId !== null) {
      setKeywordSite('');
      if (searchSiteInputRef.current) {
        searchSiteInputRef.current.value = '';
      }
      if (routePointsRef.current) {
        routePointsRef.current.scrollTop = 0;
      }
      if (searchSitesRef.current) {
        searchSitesRef.current.scrollTop = 0;
      }
      if (routeItemsRef.current[selectedRouteId]) {
        routeItemsRef.current[selectedRouteId]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
      setRouteDetail(null);
      getRouteDetail();
    } else {
      setRouteDetail(null);
      setSites([]);
    }
  }, [selectedRouteId]);

  useEffect(() => {
    searchEmissionSites();
  }, [keywordSite, routeDetail]);

  return (
    <div className={styles.container}>
      <div className={styles.routeListWrapper}>
        <div className={styles.routeListHeader}>
          <span>{t('route_settings.list')}</span>
          <span className="pointer">
            <IconAddCircleOutline
              stroke="#e7e7e7"
              size={32}
              onClick={handleAddRoute}
            />
          </span>
        </div>
        <div className={styles.searchRoute}>
          <div className={`${styles.search} ${styles.block}`}>
            <IconSearch color="#666" size={20} />
            <input
              placeholder={t('route_settings.search_route_placeholder') || ''}
              onChange={(e) => handleOnchangeKeywordRoute(e.target.value)}
            />
          </div>
        </div>
        <div className={styles.routeList}>
          {routes.length === 0 ? (
            <div style={{ marginTop: '16px' }}>{t('messages.M_008')}</div>
          ) : (
            routes.map((route) => (
              <div
                key={route.id}
                ref={(el) => {
                  routeItemsRef.current[route.id] = el;
                }}
              >
                <RouteItem
                  route={route}
                  isActive={route.id === selectedRouteId}
                  onClick={() => {
                    setSelectedRouteId(route.id);
                  }}
                  onEdit={() => handleEditRoute(route.id)}
                  onDelete={(routeId, routeName) =>
                    setConfirmDeleteModal({
                      routeId: routeId,
                      routeName: routeName,
                    })
                  }
                />
              </div>
            ))
          )}
        </div>
      </div>

      <div className={styles.routeDetailWrapper}>
        <div className={styles.routeDetailHeader}>
          <div className={styles.title}>
            <h2>{routeDetail ? routeDetail.routeName : ''}</h2>
            <span>
              {t('route_settings.assigned')}{' '}
              {routeDetail
                ? routeDetail.drivers.map((driver) => driver.name).join(', ')
                : t('messages.M_008')}
            </span>
          </div>

          <div className={styles.filters}>
            <div className={styles.blockTitle}>
              {t('route_settings.collect_material')}
            </div>
            <div className={styles.tags}>
              {routeDetail && routeDetail.materialNames.length > 0 ? (
                routeDetail.materialNames.map((material, index) => (
                  <Popover
                    key={index}
                    content={material}
                    autoGen={{
                      lines: 1,
                      odd: 4,
                      wrapperStyle: {
                        minWidth: 'fit-content',
                        maxWidth: '200px',
                      },
                    }}
                    positions={['top']}
                    containerStyle={{
                      maxWidth: '600px',
                      wordBreak: 'break-all',
                    }}
                  >
                    <p key={material} className={styles.tag}>
                      {material}
                    </p>
                  </Popover>
                ))
              ) : (
                <span className={styles.noData}>{t('messages.M_008')}</span>
              )}
            </div>
          </div>
        </div>

        <div className={styles.routeDetail}>
          <div className={styles.routePointsWrapper}>
            <span className={styles.blockTitle}>
              {t('route_settings.route_sites')}
            </span>
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="sites">
                {(provided) => (
                  <div
                    className={styles.routePoints}
                    {...provided.droppableProps}
                    ref={(el) => {
                      if (typeof provided.innerRef === 'function') {
                        provided.innerRef(el);
                      }
                      if (routePointsRef) {
                        (
                          routePointsRef as React.MutableRefObject<HTMLDivElement | null>
                        ).current = el;
                      }
                    }}
                  >
                    {sites.length === 0 ? (
                      <div className={styles.noData}>
                        {t('route_settings.no_route_site')}
                      </div>
                    ) : (
                      sites.map((site, index) => (
                        <Draggable
                          key={site.id}
                          draggableId={site.id.toString()}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                            >
                              <SiteItem
                                site={site}
                                index={index}
                                onRemove={() => handleRemoveSite(site.id)}
                                dragHandleProps={provided.dragHandleProps}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            <button
              className={styles.saveRouteSitesButton}
              onClick={saveCollectRouteSites}
            >
              {t('route_settings.save_btn')}
            </button>
          </div>

          <div className={styles.searchSite}>
            <div className={`${styles.search} ${styles.block}`}>
              <IconSearch color="#666" size={20} />
              <input
                ref={searchSiteInputRef}
                placeholder={t('route_settings.search_site_placeholder') || ''}
                onChange={(e) => handleOnchangeKeywordSite(e.target.value)}
              />
            </div>

            <div className={styles.placeList} ref={searchSitesRef}>
              {emissionSites.filter(
                (site) => !sites.some((s) => s.id === site.id)
              ).length === 0 ? (
                <div className={styles.noData}>{t('messages.M_008')}</div>
              ) : (
                emissionSites
                  .filter((site) => !sites.some((s) => s.id === site.id))
                  .map((site) => (
                    <SearchSiteItem
                      key={site.id}
                      site={site}
                      onAddSite={() => handleAddSite(site)}
                    />
                  ))
              )}
            </div>
          </div>
        </div>
      </div>
      {createUpdateModal.isOpen && (
        <CreateUpdateRouteModal
          onClose={() => setFormModal(false, ModalType.add)}
          createUpdateModal={createUpdateModal}
          onSubmit={handleSubmit}
        />
      )}

      {confirmDeleteModal && (
        <DeleteItemDarkModal
          onClose={() => {
            setConfirmDeleteModal(undefined);
          }}
          onHandleDelete={handleDeleteRoute}
          content={
            confirmDeleteModal?.routeName
              ? t('common.confirm_delete', {
                  field: confirmDeleteModal.routeName,
                })
              : ''
          }
          closeModalIcon={<IconClose />}
        />
      )}
    </div>
  );
};

export default RouteSettingManagement;
